import type { DCSContext, SlotPlacementId, Step } from '@de/types/types';
import { StepType } from '@de/types/enums';
import { getTypeInfo } from '@de/utils/getTypeInfo';
import type { EventContext as PayloadEventContext } from '@de/utils/createEventingPayloadsDE';
import {
  buildCustomDimensions,
  type CustomDimension,
} from './customDimensions';
import type { MonarchIdentity } from './monarchIdentity';
import { resolveActionOutcome } from './actionOutcome';
import { getExperienceVariant } from './getExperienceVariant';

/**
 * Form name shared by every Form/Field event in the dynamic-experience flow,
 * per §6 / §8 / §9 of the Dynamic Experience Eventing Requirements.
 */
export const DEFAULT_FORM_NAME = 'dexp-question-flow';

/**
 * `EventContext` shape consumed by everything downstream of the provider:
 * `useFlowTrackingEvents`, `Experience.tsx`, `useHandleStepForward`,
 * `useHandleSubmit`, `createEventingPayloadsDE`, etc.
 *
 * Keep this aligned with the `EventContext` exported by
 * `createEventingPayloadsDE` — fields here are a superset that the provider's
 * memo populates today. Pre-existing public consumers continue to receive the
 * same object shape. `dcs` is narrowed to the provider's `DCSContext` to keep
 * `useDynamicExperienceContext().eventContext.dcs` typed as it always was.
 */
export interface DynamicExperienceEventContext
  extends Omit<PayloadEventContext, 'dcs'> {
  dcs?: DCSContext;
  formName?: string;
  stepName?: string;
}

interface BuildEventContextParams {
  steps: Step[];
  currentStep?: Step;
  currentStepType?: string;
  currentStepIndex?: number;
  dcs?: DCSContext;
  placementId?: SlotPlacementId;
  elementLoadedCorrelationId: string;
  viewCorrelationId: string;
  formViewedCorrelationId: string;
  providedCustomDimensions?: CustomDimension[];
  // Raw Monarch identity; `slotId`/`experienceVariant` are derived below.
  monarch?: MonarchIdentity;
}

/**
 * Compose the `eventContext` value held on the React context. Pure function —
 * given identical inputs returns an identical (referentially distinct) object.
 *
 * Spec compliance:
 * - `actionOutcome` is `dexp-question` / `dexp-results` per §6.4 (replaces
 *   the previous `flowId`-based heuristic that lived in the provider).
 * - `customDimensions` carry the required `sourceCorrelationId` chain (§11).
 * - `formName` is `'dexp-question-flow'` for every entry-point event (§6 / §9).
 */
export const buildEventContext = ({
  steps,
  currentStep,
  currentStepType,
  currentStepIndex,
  dcs,
  placementId,
  elementLoadedCorrelationId,
  viewCorrelationId,
  formViewedCorrelationId,
  providedCustomDimensions,
  monarch,
}: BuildEventContextParams): DynamicExperienceEventContext => {
  const { formatType, formatSubtype } = getTypeInfo(
    currentStepType,
    currentStep as Step,
  );

  const slotId = placementId || '';
  const experienceVariant = getExperienceVariant(
    currentStepType,
    currentStep,
    monarch?.slotVariant,
  );

  // Results-page product events anchor `sourceCorrelationId` on
  // `formViewedCorrelationId` (stitch back to the form session); entry stays on
  // `elementLoadedCorrelationId`. Mirrors `createResultsEventContext`.
  const isResultsSurface =
    currentStep?.type === StepType.RESULT && currentStep?.isEntry !== true;
  const customDimensions = buildCustomDimensions({
    elementLoadedCorrelationId,
    currentStep,
    sourceCorrelationId: isResultsSurface
      ? formViewedCorrelationId
      : elementLoadedCorrelationId,
    providedCustomDimensions,
    monarch,
    slotId,
    experienceVariant,
  });

  return {
    dcs,
    placement: placementId || '',
    currentStep,
    currentStepType,
    currentStepIndex,
    formName: DEFAULT_FORM_NAME,
    stepName: currentStep?.title,
    actionOutcome: resolveActionOutcome(currentStep, steps),
    formatType,
    formatSubtype,
    experienceVariant,
    elementLoadedCorrelationId,
    viewCorrelationId,
    formViewedCorrelationId,
    customDimensions,
  };
};
